import React, { useState } from 'react'
import { Modal, Form, Alert, Button } from 'react-bootstrap'

function ReorderVisitsPopup(props) {
    const initMapping = () => {
        const m = {}
        Object.keys(props.data).forEach(v => {
            const num = parseInt(v.match(/\d+/)[0])
            m[num] = num
        })
        return m
    }
    const [mapping, setMapping] = useState(initMapping)
    const [committed, setCommitted] = useState(initMapping)
    const [error, setError] = useState(null)

    const values = Object.values(mapping).map(v => parseInt(v)).filter(v => !isNaN(v))
    const hasDuplicates = values.length !== new Set(values).size
    const hasInvalid = Object.values(mapping).some(v => isNaN(parseInt(v)) || parseInt(v) < 1)
    const isUnchanged = Object.keys(mapping).every(k => parseInt(mapping[k]) === parseInt(k))
    const canSubmit = !hasDuplicates && !hasInvalid && !isUnchanged

    const handleChange = (oldNum, val) => {
        setMapping(prev => ({ ...prev, [oldNum]: val }))
    }

    const handleShift = (oldNum) => {
        const fromVal = parseInt(committed[oldNum])
        const newVal = parseInt(mapping[oldNum])
        if (isNaN(newVal) || newVal < 1) return

        const newMapping = { ...mapping }
        Object.keys(newMapping).forEach(k => {
            const key = parseInt(k)
            if (key === oldNum) return
            const v = parseInt(newMapping[k])
            if (isNaN(v)) return
            if (newVal < fromVal) {
                if (v >= newVal && v <= fromVal - 1) newMapping[k] = v + 1
            } else {
                if (v >= newVal) newMapping[k] = v + 1
            }
        })

        setMapping(newMapping)
        setCommitted(newMapping)
    }

    const handleSubmit = () => {
        setError(null)
        const numericMapping = {}
        Object.keys(mapping).forEach(k => { numericMapping[k] = parseInt(mapping[k]) })
        props.onReorder(numericMapping, setError)
    }

    const handleHide = () => {
        const init = initMapping()
        setMapping(init)
        setCommitted(init)
        setError(null)
        props.onHide()
    }

    // Build sorted list of existing visit keys
    const sortedKeys = Object.keys(props.data).sort((a, b) => parseInt(a.match(/\d+/)[0]) - parseInt(b.match(/\d+/)[0]))

    // Interleave the new visit row at the position it would occupy among original numbers
    const tableRows = []
    let newRowInserted = !props.newVisitTarget
    sortedKeys.forEach(v => {
        const oldNum = parseInt(v.match(/\d+/)[0])
        if (!newRowInserted && props.newVisitTarget <= oldNum) {
            tableRows.push({ isNew: true })
            newRowInserted = true
        }
        tableRows.push({ isNew: false, key: v, oldNum })
    })
    if (!newRowInserted) {
        tableRows.push({ isNew: true })
    }

    return (
        <Modal show={props.show} onHide={handleHide} dialogClassName="modal-reorder">
            <Modal.Header closeButton>
                <Modal.Title>
                    {props.newVisitTarget
                        ? <>Arrange Visits — Inserting <strong>Visit {props.newVisitTarget}</strong></>
                        : 'Reorder All Visits'
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{maxHeight:'70vh', overflowY:'auto'}}>
                {props.newVisitTarget &&
                    <p className="text-muted mb-3" style={{fontSize:'0.9rem'}}>
                        The new visit is shown below. Move the existing visits to free up the slot — use <strong>Shift others ↓</strong> to cascade automatically.
                    </p>
                }
                {error && <Alert variant="danger">{error}</Alert>}
                {hasDuplicates && <Alert variant="warning" className="mb-3">Duplicate visit numbers — resolve before applying.</Alert>}

                <table style={{width:'100%', borderCollapse:'separate', borderSpacing:'0 4px'}}>
                    <thead>
                        <tr style={{color:'#6c757d', fontSize:'0.8rem', textTransform:'uppercase', letterSpacing:'0.05em'}}>
                            <th style={{paddingBottom:'6px', width:'35%'}}>Visit</th>
                            <th style={{paddingBottom:'6px', width:'8%'}}></th>
                            <th style={{paddingBottom:'6px', width:'22%'}}>New Number</th>
                            <th style={{paddingBottom:'6px', width:'35%'}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((row, i) => {
                            if (row.isNew) {
                                return (
                                    <tr key="new-visit-row">
                                        <td colSpan="4" style={{padding:'0 0 4px 0'}}>
                                            <div style={{
                                                background:'#0d6efd',
                                                color:'white',
                                                borderRadius:'6px',
                                                padding:'10px 14px',
                                                display:'flex',
                                                alignItems:'center',
                                                gap:'10px'
                                            }}>
                                                <span style={{
                                                    background:'white',
                                                    color:'#0d6efd',
                                                    borderRadius:'4px',
                                                    padding:'2px 8px',
                                                    fontWeight:700,
                                                    fontSize:'0.75rem',
                                                    letterSpacing:'0.05em',
                                                    whiteSpace:'nowrap'
                                                }}>NEW</span>
                                                <span style={{fontWeight:600}}>
                                                    Visit {props.newVisitTarget} — will be inserted here
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }

                            const { key: v, oldNum } = row
                            const val = mapping[oldNum]
                            const newNum = parseInt(val)
                            const isDup = values.filter(x => x === newNum).length > 1
                            const isInvalid = isDup || isNaN(newNum) || newNum < 1
                            const isChanged = newNum !== oldNum && !isNaN(newNum)
                            const visitDate = props.data[v].visits && props.data[v].visits.date ? props.data[v].visits.date : null
                            const rowBg = isInvalid ? '#fff3cd' : isChanged ? '#f0fff4' : 'transparent'

                            return (
                                <tr key={i} style={{background: rowBg, borderRadius:'6px', transition:'background 0.2s'}}>
                                    <td style={{padding:'8px 10px', borderRadius:'6px 0 0 6px'}}>
                                        <div style={{fontWeight: isChanged ? 600 : 400}}>{v}</div>
                                        <small style={{color:'#6c757d'}}>{visitDate || 'No date recorded'}</small>
                                    </td>
                                    <td style={{textAlign:'center', color:'#adb5bd', fontSize:'1.1rem'}}>→</td>
                                    <td style={{padding:'4px 6px'}}>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            value={val}
                                            onChange={e => handleChange(oldNum, e.target.value)}
                                            isInvalid={isInvalid}
                                            isValid={isChanged && !isInvalid}
                                            style={{fontWeight: isChanged ? 600 : 400}}
                                        />
                                        {isChanged && !isInvalid &&
                                            <small style={{color:'#198754'}}>Visit {newNum}</small>
                                        }
                                    </td>
                                    <td style={{padding:'4px 8px', borderRadius:'0 6px 6px 0'}}>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => handleShift(oldNum)}
                                            disabled={isNaN(newNum) || newNum < 1}
                                            title="Push other visits out of the way to make room for this number"
                                            style={{whiteSpace:'nowrap', width:'100%'}}
                                        >
                                            Shift others ↓
                                        </Button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>

                <div style={{marginTop:'12px', fontSize:'0.8rem', color:'#6c757d'}}>
                    <strong>Shift others ↓</strong> — automatically increments all other visits whose new number is ≥ this row's value.
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => { const init = initMapping(); setMapping(init); setCommitted(init); setError(null) }} className="me-auto">
                    Reset
                </Button>
                <Button variant="secondary" onClick={handleHide}>Cancel</Button>
                <Button variant="warning" onClick={handleSubmit} disabled={!canSubmit}>Apply Changes</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ReorderVisitsPopup
